import React from 'react';
import { Skeleton, useTheme, Box, keyframes } from '@mui/material';

// Shimmer animation for skeleton
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

function RecipeExpandedSkeleton() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Dynamic background with subtle gradient
  const cardBgGradient = isDark
    ? "linear-gradient(135deg, rgba(30, 30, 35, 1) 0%, rgba(20, 20, 25, 1) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 1) 100%)";
  
  const skeletonBg = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)";
  const skeletonHighlight = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";

  return (
    <Box
      sx={{
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        padding: "2px",
        borderRadius: "18px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Inner card */}
      <Box
        sx={{
          background: cardBgGradient,
          borderRadius: "16px",
          overflow: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header Section - title, source, dots menu */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: ".75em",
            flexShrink: 0,
          }}
        >
          {/* Left - Title and source */}
          <Box sx={{ flex: 1 }}>
            {/* Title */}
            <Skeleton 
              variant="text" 
              width="55%" 
              height={26} 
              sx={{ 
                borderRadius: "6px",
                bgcolor: skeletonBg,
                "&::after": {
                  background: `linear-gradient(90deg, transparent, ${skeletonHighlight}, transparent)`,
                  animation: `${shimmer} 1.5s ease-in-out infinite`,
                }
              }} 
            />
            {/* Source name */}
            <Skeleton
              variant="text"
              width="35%"
              height={18}
              sx={{ 
                marginTop: "4px",
                borderRadius: "4px",
                bgcolor: skeletonBg,
              }}
            />
          </Box>
          
          {/* Right - Dots menu placeholder */}
          <Skeleton 
            variant="rectangular" 
            width={32}
            height={32}
            sx={{
              borderRadius: "8px",
              bgcolor: skeletonBg,
              flexShrink: 0,
              ml: 1,
            }}
          />
        </Box>

        {/* Description - 2 lines */}
        <Box sx={{ px: "1em", pb: "1em", flexShrink: 0 }}>
          <Skeleton variant="text" width="100%" height={16} sx={{ bgcolor: skeletonBg }} />
          <Skeleton variant="text" width="70%" height={16} sx={{ mt: 0.5, bgcolor: skeletonBg }} />
        </Box>

        {/* Two-column layout: Image left, content right */}
        <Box
          sx={{
            display: "flex",
            gap: 3,
            px: "1em",
            pb: "1em",
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Left column - Image */}
          <Box
            sx={{
              width: "40%",
              flexShrink: 0,
            }}
          >
            <Skeleton 
              variant="rectangular" 
              width="100%"
              height={350}
              sx={{ 
                borderRadius: "12px",
                bgcolor: skeletonBg,
                "&::after": {
                  background: `linear-gradient(90deg, transparent, ${skeletonHighlight}, transparent)`,
                  animation: `${shimmer} 1.5s ease-in-out infinite`,
                }
              }} 
            />
          </Box>

          {/* Right column - Tabs and content */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Tab bar placeholder */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                pb: 1,
                mb: 2,
                borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              }}
            >
              <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: skeletonBg }} />
              <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: skeletonBg }} />
              <Skeleton variant="text" width={50} height={20} sx={{ bgcolor: skeletonBg }} />
            </Box>

            {/* Content placeholder - simple lines */}
            <Box sx={{ flex: 1 }}>
              {Array.from({ length: 7 }).map((_, idx) => (
                <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                  <Skeleton variant="text" width={`${60 + Math.random() * 25}%`} height={18} sx={{ bgcolor: skeletonBg }} />
                  <Skeleton variant="text" width={60} height={18} sx={{ bgcolor: skeletonBg }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default RecipeExpandedSkeleton;
